import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
    ColDef,
    CellEditingStoppedEvent,
    ICellRendererParams,
    GridApi,
    SelectionChangedEvent,
} from "ag-grid-community";
import API from "../api/axiosInstance";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAppTheme } from "../themes/ThemeContext";
import "../styles/TableViewer.css";
import RelatedRecordsPopup from "../components/RelatedRecordsPopup";
import {
    Box, Button,
    Container,
    Typography,
    useTheme,
    TextField,
    Autocomplete,
    Modal,
    Fade,
    Backdrop, FormControlLabel, Switch
} from "@mui/material";
import useMediaQuery from '@mui/material/useMediaQuery';

//import "ag-grid-community/styles/ag-grid.css";
//import "ag-grid-community/styles/ag-theme-alpine.css";
//import "ag-grid-community/styles/ag-theme-alpine-dark.css";

const TableViewer: React.FC = () => {
    const { tableId } = useParams<{ tableId: string }>();
    const navigate = useNavigate();
    const { mode } = useAppTheme();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [tables, setTables] = useState<any[]>([]);
    const [showPublished, setShowPublished] = useState(true);
    const [showDeleted, setShowDeleted] = useState(false);

    const [selectedRow, setSelectedRow] = useState<any | null>(null);
    const [relatedRecord, setRelatedRecord] = useState<any | null>(null);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [cloneModalOpen, setCloneModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newTableName, setNewTableName] = useState("");
    const [addError, setAddError] = useState("");

    const filteredTables = useMemo(() => {
        if (!searchTerm.trim()) return tables;
        return tables.filter((table) =>
            table.tableName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            table.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [tables, searchTerm]);


    useEffect(() => {
        const initialize = async () => {
            const userRaw = localStorage.getItem('user');
            if (!userRaw) return navigate("/login");

            const user = JSON.parse(userRaw);
            if (!user?.user?.authtoken || !user?.user?.sessionId) return navigate("/login");

            await fetchTables();
        };

        initialize();
    }, [tableId, navigate]);


    const tableColumns: ColDef[] = useMemo(() => [
        {
            field: 'tableName',
            headerName: 'Table Name',
            flex: 1,
            sortable: true,
            filter: true,
            cellStyle: {
                fontSize: '1.1rem',  // or '18px'
                fontWeight: 'bold',
            },
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 2,
            sortable: true,
            filter: true,
        },
        {
            headerName: 'Actions',
            width: 150,
            cellRenderer: (params: ICellRendererParams) => (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/table/${params.data.tableID}/view`)}
                >
                    Open
                </Button>
            ),
        },
    ], [navigate]);


    const fetchTables = async () => {
        try {
            setLoading(true);
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = {
                headers: {
                    authToken: user.user.authtoken,
                    sessionId: user.user.sessionId,
                },
            };

            let visibility = "published";
            if (!showPublished) visibility = "unpublished";
            if (!showPublished && showDeleted) visibility = "all";

            const response = await API.get(`/tables?visibility=${visibility}`, headers);
            setTables(response.data);
            console.log("Fetched tables:", response.data);
        } catch (error) {
            console.error("Error fetching tables:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTables();
    }, [showPublished, showDeleted]);

    const handleRowEdit = async (event: CellEditingStoppedEvent) => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const headers = {
                headers: {
                    authToken: user.user.authtoken,
                    sessionId: user.user.sessionId
                }
            };

            await API.put(`/tables/${tableId}/records/${event.data.id}`, event.data, headers);
        } catch (error) {
            console.error("Error updating record:", error);
        }
    };

    const handleDelete = async () => {
        if (!selectedRow) return;

        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const headers = {
                headers: {
                    authToken: user.user.authtoken,
                    sessionId: user.user.sessionId
                }
            };

            await API.delete(`/tables/delete/${selectedRow.tableID}`, headers);
            setSelectedRow(null);
            setDeleteModalOpen(false);
            await fetchTables(); 
        } catch (error) {
            console.error("Error deleting record:", error);
        }
    };


    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleAddTable = async () => {
        if (!newTableName.trim()) {
            setAddError("Table name is required.");
            return;
        }

        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const headers = {
                headers: {
                    authToken: user.user.authtoken,
                    sessionId: user.user.sessionId
                }
            };

            const response = await API.post("/tables", { TableName: newTableName, Description: "" }, headers);

            // On success, redirect to the table editor for the new table
            navigate(`/tableeditor/${response.data.tableId}`);
        } catch (error: any) {
            console.error("Error creating table:", error);
            setAddError(error?.response?.data?.message || "Failed to create table.");
        }
    };


    const rowSelection = useMemo(() => ({
        mode: 'singleRow',
        checkboxes: false,
        enableClickSelection: true,
    }), []);

    const isRowSelected = selectedRow !== null;

    return (
        <Box
            bgcolor={theme.palette.background.default}
            color={theme.palette.text.primary}
            minHeight="100vh"
            px={3}
            py={6} // Add padding from the top (e.g., 32px)
        >


            <Container maxWidth="lg" sx={{ flex: 1, mt: 4, mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Table Viewer
                </Typography>

                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    mb={2}
                >
                    {/* Top row: Search + Toggles */}
                    <Box
                        display="flex"
                        flexDirection={isMobile ? "column" : "row"}
                        gap={2}
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        {/* SEARCH FIELD */}
                        {tables.length > 0 && (
                            <TextField
                                label="Search Tables"
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={handleSearch}
                                sx={{ flex: 1, maxWidth: 400, width: isMobile ? "100%" : 300 }}
                            />
                        )}

                        {/* TOGGLES */}
                        <Box display="flex" gap={2} flexWrap="wrap">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showPublished}
                                        onChange={(e) => {
                                            setShowPublished(e.target.checked);
                                            setSelectedRow(null); // Clear selection on toggle
                                        }}
                                        size="small"
                                    />
                                }
                                label="Published"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showDeleted}
                                        onChange={(e) => {
                                            setShowDeleted(e.target.checked);
                                            setSelectedRow(null); // Clear selection on toggle
                                        }}
                                        size="small"
                                    />
                                }
                                label="Show Deleted"
                            />
                        </Box>
                    </Box>

                    {/* ACTION BUTTONS */}
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <Box display="flex" gap={1} flexWrap="wrap">
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    setShowAddModal(true);
                                    setNewTableName("");
                                    setAddError("");
                                }}
                            >
                                Add
                            </Button>

                            {isRowSelected && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() =>
                                            navigate(`/tableeditor?tableId=${selectedRow?.tableID}`)
                                        }
                                    >
                                        Edit
                            </Button>
                            )}
                            {isRowSelected && !selectedRow?.published && (
                                <>

                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => setCloneModalOpen(true)}
                                    >
                                        Clone
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="error"
                                        onClick={() => setDeleteModalOpen(true)}
                                    >
                                        Delete
                                    </Button>
                                </>
                            )}
                        </Box>

                    </Box>
                </Box>


                {(!tables || tableColumns.length < 1 || tables.length < 1 || loading) ? (
                    <Typography>Loading records...</Typography>
                ) : (
                    <div
                        className={`ag-theme-${mode === "dark" ? "alpine-dark" : "alpine"}`}
                        style={{ height: 500, width: "100%" }}
                    >
                        <AgGridReact
                            columnDefs={tableColumns}
                                rowData={filteredTables}
                            onGridReady={(params) => setGridApi(params.api)}
                            onCellEditingStopped={handleRowEdit}
                                rowSelection="single"
                                onRowDoubleClicked={(event) => {
                                    const tableID = event.data?.tableID;
                                    if (tableID) {
                                        navigate(`/table/${tableID}/view`);
                                    }
                                }}
                            onSelectionChanged={(event: SelectionChangedEvent) => {
                                const selectedNodes = event.api.getSelectedNodes();
                                setSelectedRow(selectedNodes.length === 1 ? selectedNodes[0].data : null);
                            }}
                        />
                    </div>
                )}
            </Container>

            {relatedRecord && (
                <RelatedRecordsPopup
                    record={relatedRecord}
                    onClose={() => setRelatedRecord(null)}
                />
            )}

            {/* Clone Modal */}
            <Modal
                open={cloneModalOpen}
                onClose={() => setCloneModalOpen(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={cloneModalOpen}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 400,
                            bgcolor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2, // Rounded corners
                        }}
                    >
                        <Typography variant="h6" component="h2">
                            Clone Feature
                        </Typography>
                        <Typography sx={{ mt: 2 }}>
                            Not yet implemented.
                        </Typography>
                        <Box mt={3} display="flex" justifyContent="flex-end">
                            <Button variant="outlined" onClick={() => setCloneModalOpen(false)}>
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>


            {/*  Add table model */ }
            {showAddModal && (
                <div className="modal-backdrop">
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            p: 3,
                            borderRadius: 2,
                            width: 400,
                            boxShadow: 24,
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Create New Table
                        </Typography>

                        <TextField
                            label="Table Name"
                            fullWidth
                            variant="outlined"
                            value={newTableName}
                            onChange={(e) => setNewTableName(e.target.value)}
                            sx={{ mb: 2 }}
                            error={!!addError}
                            helperText={addError}
                        />

                        <Box display="flex" justifyContent="flex-end" gap={2}>
                            <Button variant="outlined" onClick={() => setShowAddModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={handleAddTable}>
                                Create
                            </Button>
                        </Box>
                    </Box>

                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Modal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={deleteModalOpen}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 400,
                            bgcolor: "background.paper",
                            boxShadow: 24,
                            p: 4,
                            color: theme.palette.text.primary, // ADD THIS LINE
                            borderRadius: 2 // ADD THIS LINE
                        }}
                    >
                        <Typography variant="h6" component="h2">
                            Confirm Deletion
                        </Typography>
                        <Typography sx={{ mt: 2 }}>
                            Are you sure you want to delete this record?
                        </Typography>
                        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleDelete}
                            >
                                Delete
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>

        </Box>
    );
};

export default TableViewer;

